// Coaching: the roster, the groups, and giving a programme to somebody.
//
// Pure logic only. The server owns every rule; this exists so a coach gets the
// answer without a round trip, and so the copy-versus-live distinction is
// decided in one place rather than in three pieces of JSX.

/** The two ways a programme can be given out. */
export const ASSIGNMENT_MODE = {
  COPY: 'copy',
  LIVE: 'live',
};

/**
 * What each mode means, in words a coach can act on.
 *
 * This is the single most important thing this screen has to communicate. The
 * difference is invisible until it bites: editing a copy affects one athlete,
 * editing a live calendar affects the whole squad, and by the time that is
 * surprising it has already happened.
 */
export function describeMode(mode) {
  if (mode === ASSIGNMENT_MODE.LIVE) {
    return {
      title: 'Shared calendar',
      detail: 'Everyone in the group follows this programme. Editing it changes '
        + 'what all of them see. Their logged sets stay their own.',
    };
  }
  return {
    title: 'Their own copy',
    detail: 'The athlete gets a duplicate. It diverges the moment either of you '
      + 'edits it, and your original is untouched. They start with an empty log.',
  };
}

/**
 * What the coach is told after inviting somebody, whoever the email belongs to.
 *
 * Deliberately the same sentence for every outcome, because the server's answer
 * is the same for every outcome: saying "invited Alice" for a real account and
 * "no such account" for an unknown one is how the old endpoint told strangers
 * which emails existed. It also cannot say "sent" — nothing is emailed. The
 * invitation waits on the athlete's home screen.
 */
export const INVITATION_SENT = 'Invitation made. If that email has an account, they will '
  + 'see it on their home screen and join your roster once they accept.';

/** Validate the add-an-athlete form. Returns a field-to-message map. */
export function validateAthleteForm(form) {
  const errors = {};
  const email = String(form?.email ?? '').trim();

  if (!email) {
    errors.email = 'Enter their email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'That does not look like an email address.';
  }

  return errors;
}

/** The request body for adding an athlete. */
export function buildAthleteRequest(form) {
  return {
    email: String(form?.email ?? '').trim(),
    displayName: String(form?.displayName ?? '').trim(),
  };
}

/** Validate the assign form. */
export function validateAssignment(form) {
  const errors = {};

  if (!Number(form?.programId)) {
    errors.programId = 'Choose a programme.';
  }

  const mode = form?.mode ?? ASSIGNMENT_MODE.COPY;
  if (mode === ASSIGNMENT_MODE.COPY && !Number(form?.athleteId)) {
    errors.athleteId = 'Choose an athlete.';
  }
  if (mode === ASSIGNMENT_MODE.LIVE && !Number(form?.groupId)) {
    errors.groupId = 'Choose a group.';
  }

  return errors;
}

/**
 * The request body for an assignment.
 *
 * Exactly one assignee is sent — the server enforces the same rule with a CHECK,
 * and sending both would be rejected rather than silently preferring one.
 */
export function buildAssignmentRequest(form) {
  const mode = form?.mode ?? ASSIGNMENT_MODE.COPY;
  const body = {
    programId: Number(form?.programId) || 0,
    mode,
  };

  if (mode === ASSIGNMENT_MODE.LIVE) {
    body.groupId = Number(form?.groupId) || 0;
  } else {
    body.athleteId = Number(form?.athleteId) || 0;
  }

  const startDate = String(form?.startDate ?? '').trim();
  if (startDate) body.startDate = startDate;

  const notes = String(form?.notes ?? '').trim();
  if (notes) body.notes = notes;

  return body;
}

/** Validate the group form. */
export function validateGroupForm(form) {
  const errors = {};
  if (!String(form?.name ?? '').trim()) {
    errors.name = 'Give the group a name.';
  }
  return errors;
}

/**
 * The request body for a group.
 *
 * A null calendar is sent explicitly rather than omitted: PUT is a full replace,
 * so omitting it would be indistinguishable from "leave it", and detaching a
 * squad from its block has to be expressible.
 */
export function buildGroupRequest(form) {
  const calendar = Number(form?.calendarProgramId) || 0;
  return {
    name: String(form?.name ?? '').trim(),
    calendarProgramId: calendar > 0 ? calendar : null,
  };
}

/**
 * Which athletes are not yet in a group, for the add-member picker.
 *
 * Offering somebody already subscribed is not an error server-side — it returns
 * the same group — but it reads as a broken list.
 */
export function athletesNotIn(roster, group) {
  const members = new Set(group?.members ?? []);
  return (roster ?? []).filter((athlete) => !members.has(athlete.username));
}

/**
 * A one-line summary of an assignment, for a list row.
 */
export function describeAssignment(assignment) {
  if (!assignment) return '';

  const who = assignment.athleteName
    || (assignment.groupId ? 'a group' : 'somebody');
  const mode = assignment.mode === ASSIGNMENT_MODE.LIVE ? 'follows' : 'has a copy of';
  const name = assignment.programName || `programme ${assignment.programId}`;

  return `${who} ${mode} ${name}`;
}

/** Whether an assignment is still current. */
export function isActive(assignment) {
  return assignment?.status === 'active';
}
